import React, { useState, useEffect, useMemo } from "react";
import Loader from "../../Shared/Loader";

import "../../../css/FormsTable.css";
import Modal from "./Modal";
import AddForm from "./AddForm";
import { FormData } from "../../../types/index";
import ViewFormModal from "./ViewFormModal";
import api from "../../../api/axiosConfig";

const FormTable: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [selectedForm, setSelectedForm] = useState<FormData | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [forms, setForms] = useState<FormData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // State for filters and search
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [categoryFilter, setCategoryFilter] = useState("All");

  // 🔽 1. State for Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10; // You can make this dynamic if you want

  const fetchForms = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = sessionStorage.getItem("accessToken");
      const res = await api.auth_api.get<FormData[]>("/forms", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setForms(res.data);
    } catch (err: any) {
      console.error("Error fetching forms:", err);
      setError(
        err.response?.data?.message ||
          "Could not load forms. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchForms();
  }, []);

  const filteredForms = useMemo(() => {
    let tempForms = forms;

    if (searchTerm) {
      tempForms = tempForms.filter((form) =>
        form.name.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    if (statusFilter !== "All") {
      const shouldBeActive = statusFilter === "Enabled";
      tempForms = tempForms.filter(
        (form) => (form.isActive.toString() === "true") === shouldBeActive,
      );
    }

    if (categoryFilter !== "All") {
      tempForms = tempForms.filter((form) => form.category === categoryFilter);
    }

    return tempForms;
  }, [forms, searchTerm, statusFilter, categoryFilter]);

  // 🔽 4. Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, categoryFilter]);

  const uniqueCategories = useMemo(() => {
    const categories = new Set(forms.map((form) => form.category));
    return ["All Types", ...Array.from(categories)];
  }, [forms]);

  // 🔽 2. Calculate pagination variables
  const totalPages = Math.ceil(filteredForms.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentFormsToDisplay = filteredForms.slice(startIndex, endIndex);

  const handleAddForm = (data: any) => {
    setShowModal(false);
    fetchForms();
  };

  const handleRowClick = (form: FormData) => {
    setSelectedForm(form);
    setIsViewModalOpen(true);
  };

  const handleEdit = async () => {
    await fetchForms();
  };

  return (
    <div className="forms-table-container">
      {" "}
      {/* Renamed */}
      {/* Header with search + filters + button */}
      <div className="forms-table-text-div">
        {" "}
        {/* Renamed */}
        <div className="forms-table-text">Forms</div> {/* Renamed */}
        {/* <button
          className="forms-table-add-btn"
          onClick={() => setShowModal(true)}
        >
          + Add New Form
        </button> */}
      </div>
      <div className="forms-table-header">
        {" "}
        {/* Renamed */}
        <div className="forms-table-filters-container">
          <div className="search-input-wrapper">
            <svg
              className="search-icon"
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#94a3b8"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input
              type="text"
              placeholder="Search form..."
              className="styled-search-box"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            className="styled-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="All">All Status</option>
            <option value="Enabled">Enabled</option>
            <option value="Disabled">Disabled</option>
          </select>
          <select
            className="styled-select"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            {uniqueCategories.map((category) => (
              <option
                key={category}
                value={category === "All Types" ? "All" : category}
              >
                {category}
              </option>
            ))}
          </select>
        </div>
      </div>
      {/* Table */}
      {loading ? (
        <Loader text="Loading forms..." />
      ) : error ? (
        <p className="forms-table-error">{error}</p> /* Renamed */
      ) : (
        <table className="forms-table-element">
          {" "}
          {/* Renamed */}
          <thead>
            <tr>
              <th>Form Name</th>
              <th>Created On</th>
              <th>Disabled On</th>
              <th>Version No.</th>
              <th>Category</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {currentFormsToDisplay.length > 0 ? (
              currentFormsToDisplay.map((form, index) => (
                <tr
                  key={form._id || index}
                  onClick={() => handleRowClick(form)}
                  style={{ cursor: "pointer" }}
                >
                  <td data-label="Form Name">{form.name}</td>
                  <td data-label="Created On">
                    {form.createdAt
                      ? new Date(form.createdAt).toLocaleDateString()
                      : "-"}
                  </td>
                  <td data-label="Disabled On">
                    {form.disabledOn
                      ? new Date(form.disabledOn).toLocaleDateString()
                      : "-"}
                  </td>
                  <td data-label="Version No.">{form.version}</td>
                  <td data-label="Category">{form.category}</td>
                  <td data-label="Status">
                    <span
                      className={
                        form.isActive
                          ? "forms-table-status-enable"
                          : "forms-table-status-disable"
                      }
                    >
                      {" "}
                      {/* Renamed */}
                      {form.isActive ? "Enabled" : "Disabled"}
                    </span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6}>No forms match the current filters.</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
      {/* Add form modal */}
      <Modal show={showModal} onClose={() => setShowModal(false)}>
        <AddForm onSubmit={handleAddForm} />
      </Modal>
      {/* View form modal */}
      {isViewModalOpen && selectedForm && (
        <ViewFormModal
          isOpen={isViewModalOpen}
          onClose={() => setIsViewModalOpen(false)}
          formData={selectedForm}
          onEdit={handleEdit}
          onDisable={() => console.log("Disable Clicked")}
        />
      )}
      {/* 🔽 3. Dynamic Pagination Controls */}
      {totalPages > 1 && (
        <div className="forms-table-pagination">
          {" "}
          {/* Renamed */}
          <button
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          {Array.from({ length: totalPages }, (_, index) => index + 1).map(
            (pageNumber) => (
              <button
                key={pageNumber}
                onClick={() => setCurrentPage(pageNumber)}
                className={currentPage === pageNumber ? "active" : ""}
              >
                {pageNumber}
              </button>
            ),
          )}
          <button
            onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default FormTable;
